/**
 * Centralised navigation configuration for the Helix sidebar.
 *
 * Instead of four hardcoded arrays scattered inside Sidebar.tsx, every
 * navigation item is defined once in a registry (`NAV_ITEMS`) and then
 * referenced by key in the per-mode ordering arrays. The public
 * `buildNavigation()` function selects the right ordering at runtime.
 */

import {
  LayoutDashboard,
  TrendingUp,
  TrendingDown,
  Settings,
  Calendar,
  ShoppingCart,
  CheckCircle,
  Users,
  FileText,
  History,
  ClipboardList,
  Newspaper,
  UserCog,
  Upload,
  UserPlus,
  AlertTriangle,
  Truck,
  BarChart3,
  Building2,
  Table,
  LineChart,
  FolderOpen,
  AlertOctagon,
  Database,
  PieChart,
  Box,
  Factory,
  Package,
  PackageCheck,
  CalendarClock,
} from 'lucide-react';
import type { MenuVisibility } from '../contexts/MenuSettingsContext';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type FeatureFlag =
  | 'delivery_appeals'
  | 'spend_leakage'
  | 'bulk_upload'
  | 'commodity_codes'
  | 'supplier_stock_levels'
  | 'switch_call_off_tab'
  | 'po_line_schedules'
  | 'schedule_collaboration'
  | 'allow_supplier_decline';

/** Shape consumed by the Sidebar rendering logic (unchanged from old code). */
export interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number | string;
  children?: NavItem[];
  visibilityKey?: keyof MenuVisibility;
  /**
   * Optional supplier-side override. When the user is viewing the supplier
   * sidebar, buildNavigation swaps `visibilityKey` → `supplierVisibilityKey`
   * so the item is gated by its own admin toggle instead of sharing state
   * with the customer-side key.
   */
  supplierVisibilityKey?: keyof MenuVisibility;
  tourId?: string;
  featureFlag?: FeatureFlag;
  /** Hide the item only when this feature flag is explicitly `false` (absent → shown). */
  hiddenWhenFlagFalse?: 'part_schedule';
  /** Alternate label when portal context is flipped (Customer Portal mode) */
  flipName?: string;
  /**
   * CR-6084: when true, the item is hidden unless the user is an admin
   * (Helix internal, customer admin, or supplier admin — i.e. the same
   * `canManageUsers` capability the Sidebar already computes from the user
   * object). Use for administrative / audit surfaces (User Activity History,
   * future audit logs) where exposing the menu item to plain users is itself
   * an information leak.
   */
  adminOnly?: boolean;
  /**
   * CR-7478: hide the item when this Dashboard Visibility tab is switched
   * off for customers. The Compliance tab doubles as the tenant's Compliance
   * switch, so a tenant without it does not see Supplier Onboarding.
   * Compliance-mode tenants are exempt (see isHiddenByDashboardTab).
   */
  requiresDashboardTab?: 'compliance';
  /**
   * CR-8613: the Purchasing tab this item opens (its href carries `?tab=`).
   * It hides whenever Purchasing hides that tab — see `PURCHASING_TAB_MENU_KEYS`
   * and `useSidebarNavigation`.
   */
  purchasingTab?: PurchasingTabId;
}

/** CR-8613: Purchasing tabs that have a sidebar item of their own. */
export type PurchasingTabId = 'call_off';

/**
 * CR-8613: each such tab's own Navigation Menu Management switch. Purchasing
 * hides the tab when it is off (and when the tab is off in Admin Setup →
 * Purchasing tabs), so the sidebar item has to as well.
 */
export const PURCHASING_TAB_MENU_KEYS: Record<PurchasingTabId, keyof MenuVisibility> = {
  call_off: 'callOff',
};

// ---------------------------------------------------------------------------
// Item registry
// ---------------------------------------------------------------------------
// Each unique navigation entry is defined exactly once. Items that differ
// between standard and Barratt modes (different label, icon, etc.) are
// stored as separate keys (e.g. `suppliers` vs `barrattSupplier`).
// ---------------------------------------------------------------------------

const NAV_ITEMS = {
  // ---- Common ----
  dashboard: {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    visibilityKey: 'dashboard',
    supplierVisibilityKey: 'supplierDashboard',
    tourId: 'dashboard',
  },
  documents: {
    name: 'Documents',
    href: '/documents',
    icon: FileText,
    visibilityKey: 'documents',
    supplierVisibilityKey: 'supplierDocuments',
  },

  // ---- Standard customer ----
  supplierPerformance: {
    name: 'Supplier Performance',
    flipName: 'Customer Performance',
    href: '/suppliers/performance',
    icon: TrendingUp,
    visibilityKey: 'supplierPerformance',
    // SI-16: also on the supplier sidebar (own scores, one row per customer),
    // behind its own admin toggle like Dashboard / Documents.
    supplierVisibilityKey: 'supplierSelfPerformance',
  },
  reportsCustomer: {
    name: 'Analytics',
    href: '/reports',
    icon: PieChart,
    visibilityKey: 'analytics',
  },
  calendarCustomer: {
    name: 'Calendar',
    href: '/calendar',
    icon: Calendar,
    visibilityKey: 'calendar',
  },
  purchasing: {
    name: 'Purchasing',
    href: '/purchasing',
    icon: ShoppingCart,
    visibilityKey: 'purchasing',
    tourId: 'purchasing',
  },
  partsMaster: {
    name: 'Parts Master',
    href: '/parts-master',
    icon: Package,
    visibilityKey: 'partsMaster' as keyof MenuVisibility,
  },
  partSchedule: {
    name: 'Part Schedule',
    href: '/part-schedule',
    icon: CalendarClock,
    visibilityKey: 'partSchedule' as keyof MenuVisibility,
    // Isolated ERP module — a tenant whose ERP feed has no part schedule can
    // switch it off via CompanySettings.feature_flags.part_schedule = false.
    // Installs that never set the key keep the page (explicit false only).
    hiddenWhenFlagFalse: 'part_schedule',
  },
  qualityCustomer: {
    name: 'Quality',
    href: '/quality',
    icon: CheckCircle,
    visibilityKey: 'quality',
    tourId: 'quality',
  },
  suppliers: {
    name: 'Suppliers',
    flipName: 'Customers',
    href: '/suppliers',
    icon: Users,
    visibilityKey: 'suppliers',
    tourId: 'suppliers',
  },
  onboarding: {
    name: 'Supplier Onboarding',
    flipName: 'Customer Onboarding',
    href: '/onboarding',
    icon: UserPlus,
    visibilityKey: 'suppliers',
    requiresDashboardTab: 'compliance',
  },
  userInvitationsCustomer: {
    name: 'User Invitations',
    href: '/users/invitations',
    icon: UserCog,
    visibilityKey: 'userInvitations',
  },
  userActivityHistory: {
    name: 'User Activity History',
    href: '/user-activity',
    icon: History,
    visibilityKey: 'userActivityHistory',
    // CR-6084: audit-trail surface. Hidden from non-admin users — the
    // backend endpoint enforces IsHelixOrCompanyAdmin, so a non-admin who
    // hits the route directly gets 403, but we also hide the nav item so
    // the surface isn't even discoverable.
    adminOnly: true,
  },
  questionnaire: {
    name: 'Questionnaires',
    href: '/questionnaires',
    icon: ClipboardList,
    visibilityKey: 'questionnaire',
  },
  newsReleaseCustomer: {
    name: 'News Release',
    href: '/news',
    icon: Newspaper,
    visibilityKey: 'newsRelease',
  },
  deliveryAppeals: {
    name: 'Delivery Appeals',
    href: '/delivery-appeals',
    icon: AlertTriangle,
    featureFlag: 'delivery_appeals' as FeatureFlag,
  },
  spendLeakage: {
    name: 'Spend Leakage',
    href: '/spend-leakage',
    icon: TrendingDown,
    featureFlag: 'spend_leakage' as FeatureFlag,
  },
  dataImport: {
    name: 'Data Import',
    href: '/data-import',
    icon: Database,
    visibilityKey: 'dataImport' as keyof MenuVisibility,
  },
  importMonitor: {
    name: 'Import Monitor',
    href: '/import-monitor',
    icon: Upload,
  },
  settings: {
    name: 'My Account',
    href: '/settings',
    icon: Settings,
  },
  // ---- Standard supplier ----
  myOnboarding: {
    name: 'My Onboarding',
    href: '/onboarding/my-progress',
    icon: UserPlus,
    visibilityKey: 'supplierOnboarding',
    tourId: 'my-onboarding',
  },
  supplierQuestionnaires: {
    name: 'Questionnaires',
    // Distinct route from "My Onboarding" (/onboarding/my-progress). Both items
    // previously shared the my-progress route, which made the sidebar highlight
    // both at once (isActive() matches on pathname) and always render the same
    // page. "Questionnaires" now opens the dedicated /questionnaires view so the
    // two menu items are independent destinations.
    href: '/questionnaires',
    icon: ClipboardList,
    visibilityKey: 'supplierQuestionnaires' as keyof MenuVisibility,
  },
  salesOrders: {
    name: 'Sales Orders',
    href: '/purchasing',
    icon: ShoppingCart,
    visibilityKey: 'purchaseOrders',
    tourId: 'purchasing',
  },
  // CR-8613: Call Off straight from the sidebar, under Purchasing (customer)
  // and Sales Orders (supplier) — until now only a tab inside them. Shown only
  // where call-off is on (switch_call_off_tab) and its Purchasing tab is not
  // switched off. Gated by its page's toggle, so it never leads to a page the
  // route gate refuses. A haulier keeps its own Logistics Call-Offs item.
  callOffCustomer: {
    name: 'Call Off',
    href: '/purchasing?tab=call_off&call_off_view=management',
    icon: PackageCheck,
    visibilityKey: 'purchasing',
    featureFlag: 'switch_call_off_tab' as FeatureFlag,
    purchasingTab: 'call_off' as PurchasingTabId,
  },
  callOffSupplier: {
    name: 'Call Off',
    href: '/purchasing?tab=call_off&call_off_view=management',
    icon: PackageCheck,
    visibilityKey: 'purchaseOrders',
    featureFlag: 'switch_call_off_tab' as FeatureFlag,
    purchasingTab: 'call_off' as PurchasingTabId,
  },
  // Suppliers are invited to RFQs by notification/email but had no way to
  // get back to them — the RFQ surface is a Purchasing tab on the customer
  // side only. Give the supplier sidebar its own entry (same /rfq route,
  // which is already supplier-scoped server-side).
  rfqSupplier: {
    name: 'RFQs',
    href: '/rfq',
    icon: ClipboardList,
    visibilityKey: 'rfq',
    // Own supplier-side toggle (off by default) so the customer RFQ menu
    // setting doesn't also decide what suppliers see.
    supplierVisibilityKey: 'supplierRfq',
  },
  qualitySupplier: {
    name: 'Quality',
    href: '/quality',
    icon: CheckCircle,
    visibilityKey: 'quality',
    tourId: 'quality',
  },
  customers: {
    name: 'Customers',
    href: '/customers',
    icon: Users,
    visibilityKey: 'supplierCustomers' as keyof MenuVisibility,
    tourId: 'customers',
  },
  calendarSupplier: {
    name: 'Calendar',
    href: '/calendar',
    icon: Calendar,
    visibilityKey: 'supplierCalendar' as keyof MenuVisibility,
  },
  userInvitationsSupplier: {
    name: 'User Invitations',
    href: '/users/invitations',
    icon: UserCog,
    visibilityKey: 'userInvitations',
  },
  companyProfile: {
    name: 'Company Profile',
    href: '/suppliers',
    icon: Factory,
    visibilityKey: 'companyProfile',
  },
  newsReleaseSupplier: {
    name: 'News Release',
    href: '/news',
    icon: Newspaper,
    visibilityKey: 'supplierNewsRelease' as keyof MenuVisibility,
  },
  reportsSupplier: {
    name: 'Analytics',
    href: '/reports',
    icon: PieChart,
    visibilityKey: 'analytics',
  },
  stockLevels: {
    name: 'Stock Levels',
    href: '/stock-levels',
    icon: Box,
    featureFlag: 'supplier_stock_levels' as FeatureFlag,
  },

  // ---- Barratt customer ----
  barrattCategory: {
    name: 'Category',
    href: '/barratt/category',
    icon: FolderOpen,
    children: [
      {
        name: 'Spend Data',
        href: '/barratt/spend-data',
        icon: TrendingDown,
      },
    ],
  },
  barrattSupplier: {
    name: 'Supplier',
    href: '/suppliers',
    icon: Building2,
    visibilityKey: 'suppliers',
  },
  barrattDivisionPerformance: {
    name: 'Division Performance',
    href: '/barratt/division-performance',
    icon: BarChart3,
  },
  barrattDivisionScoreReport: {
    name: 'Division Score Report',
    href: '/barratt/division-score-report',
    icon: Table,
  },
  barrattCallOffPerformance: {
    name: 'Call Off Performance',
    href: '/barratt/call-off-performance',
    icon: LineChart,
  },
  barrattDataUploadCustomer: {
    name: 'Data Upload',
    href: '/barratt/data-upload',
    icon: Database,
  },
  barrattAnalyse: {
    name: 'Analyse',
    href: '/barratt/analyse',
    icon: TrendingDown,
  },
  barrattUserLoginHistory: {
    name: 'User Login History',
    href: '/user-activity',
    icon: History,
    visibilityKey: 'userActivityHistory',
  },
  barrattSupplierUploadHistory: {
    name: 'Supplier Upload History',
    href: '/barratt/supplier-upload-history',
    icon: History,
  },

  // ---- Barratt supplier ----
  barrattCustomer: {
    name: 'Customer',
    href: '/customers',
    icon: Users,
    visibilityKey: 'customers',
  },
  barrattSalesOrders: {
    name: 'Sales Orders',
    href: '/purchasing',
    icon: Truck,
    visibilityKey: 'purchaseOrders',
  },
  barrattNCRs: {
    name: 'NCRs',
    href: '/quality',
    icon: AlertOctagon,
    visibilityKey: 'quality',
  },
  barrattDataUploadSupplier: {
    name: 'Data Upload',
    href: '/bulk-upload',
    icon: Upload,
  },

  // ---- Logistics ----
  logisticsCallOffs: {
    name: 'Logistics Call-Offs',
    href: '/logistics/call-offs',
    icon: Truck,
  },
} satisfies Record<string, Omit<NavItem, 'children'> & { children?: Omit<NavItem, 'children'>[] }>;

type NavKey = keyof typeof NAV_ITEMS;

// ---------------------------------------------------------------------------
// Ordered sequences per mode
// ---------------------------------------------------------------------------
// Each array lists the registry keys in the exact display order that the
// old hardcoded arrays used. Changing the order here changes the sidebar.
// ---------------------------------------------------------------------------

const STANDARD_CUSTOMER_ORDER: NavKey[] = [
  'dashboard',
  'supplierPerformance',
  'reportsCustomer',
  'calendarCustomer',
  'purchasing',
  'callOffCustomer',
  'partsMaster',
  'partSchedule',
  'qualityCustomer',
  'onboarding',
  'suppliers',
  'documents',
  'questionnaire',
  'newsReleaseCustomer',
  'userActivityHistory',
  'spendLeakage',
  'dataImport',
  'importMonitor',
];

const STANDARD_SUPPLIER_ORDER: NavKey[] = [
  'dashboard',
  'myOnboarding',
  'supplierQuestionnaires',
  'salesOrders',
  'callOffSupplier',
  'rfqSupplier',
  'calendarSupplier',
  'qualitySupplier',
  'customers',
  'supplierPerformance',
  'documents',
  'newsReleaseSupplier',
  'stockLevels',
];

const BARRATT_CUSTOMER_ORDER: NavKey[] = [
  'barrattCategory',
  'barrattSupplier',
  'barrattDivisionPerformance',
  'barrattDivisionScoreReport',
  'barrattCallOffPerformance',
  'documents',
  'barrattDataUploadCustomer',
  'barrattAnalyse',
  'barrattUserLoginHistory',
  'barrattSupplierUploadHistory',
];

const BARRATT_SUPPLIER_ORDER: NavKey[] = [
  'barrattCustomer',
  'barrattSalesOrders',
  'barrattNCRs',
  'documents',
  'barrattDataUploadSupplier',
];

// CR-4143: Compliance module — stripped to onboarding/questionnaires/reports only
const COMPLIANCE_CUSTOMER_ORDER: NavKey[] = [
  'dashboard',
  'onboarding',
  'suppliers',
  'questionnaire',
  'reportsCustomer',
  'documents',
];

// Compliance supplier view — supplier sees only what they need to complete
const COMPLIANCE_SUPPLIER_ORDER: NavKey[] = [
  'myOnboarding',
  'supplierQuestionnaires',
  'documents',
];

// Logistics users — a haulier carries loads, it does not trade them.
//
// CR-7732: its call-off list is now a sidebar item rather than only the page
// login lands on, which had left a haulier with no way back to its own work
// after clicking anything else.
//
// Purchasing ('salesOrders') stays, because the haulier opens the shipping
// notice it has to clear there and files its export declaration against it,
// the same way a supplier does. Which tabs it finds inside is decided in
// Purchasing.tsx: the ticket hides stock planning, stock upload, the parts
// list and forecasts from a haulier, and it has no PO lines or deliveries of
// its own. Supplier stock levels is gone for the same reason.
const LOGISTICS_ORDER: NavKey[] = [
  'dashboard',
  'logisticsCallOffs',
  'salesOrders',
  'myOnboarding',
  'supplierQuestionnaires',
  'calendarSupplier',
  'qualitySupplier',
  'customers',
  'documents',
  'newsReleaseSupplier',
];


// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

export type PortalMode = 'customer' | 'supplier';

export interface BuildNavigationOptions {
  /** Which portal the user sees: customer or supplier */
  portalMode: PortalMode;
  /** CR-5334: Whether the Spend Analytics navigation layout is active
   *  (renamed from isBarratt — the neutral module name). */
  isSpendAnalytics: boolean;
  /** Whether the application is running in Compliance mode (CR-4143) */
  isCompliance?: boolean;
  /** Whether supplier is in Customer Portal owner mode (managing customers) */
  isCustomerPortalOwner?: boolean;
  /** Whether the user belongs to a logistics company (restricted view) */
  isLogistics?: boolean;
}

/**
 * Returns the ordered list of `NavItem` objects for the given combination of
 * portal mode and Barratt flag.
 *
 * This replaces the four hardcoded arrays that previously lived in
 * `Sidebar.tsx`: `customerNavigation`, `supplierNavigation`,
 * `barrattCustomerNavigation`, and `barrattSupplierNavigation`.
 */
export function buildNavigation({ portalMode, isSpendAnalytics, isCompliance, isCustomerPortalOwner, isLogistics }: BuildNavigationOptions): NavItem[] {
  let order: NavKey[];

  if (isLogistics) {
    order = LOGISTICS_ORDER;
  } else if (isCompliance) {
    order = portalMode === 'supplier' ? COMPLIANCE_SUPPLIER_ORDER : COMPLIANCE_CUSTOMER_ORDER;
  } else if (isSpendAnalytics) {
    order = portalMode === 'supplier' ? BARRATT_SUPPLIER_ORDER : BARRATT_CUSTOMER_ORDER;
  } else {
    order = portalMode === 'supplier' ? STANDARD_SUPPLIER_ORDER : STANDARD_CUSTOMER_ORDER;
  }

  return order.map(key => {
    const item = NAV_ITEMS[key];
    const navItem: NavItem = { ...item };
    if (isCustomerPortalOwner && navItem.flipName) {
      navItem.name = navItem.flipName;
    }
    if ('children' in item && item.children) {
      navItem.children = item.children.map(child => ({ ...child }));
    }
    return navItem;
  });
}

// ---------------------------------------------------------------------------
// CR-7058: toggle-controlled pages with no sidebar entry
// ---------------------------------------------------------------------------
// User Invitations carries a Navigation Menu Management toggle but is reached
// from User Management / Supplier Group Detail rather than the sidebar, so it
// is in the registry but in no ordering array. The route gate still has to
// honour its toggle, so it resolves against these entries as well.
// ---------------------------------------------------------------------------

const GATE_ONLY_CUSTOMER: NavKey[] = ['userInvitationsCustomer'];
const GATE_ONLY_SUPPLIER: NavKey[] = ['userInvitationsSupplier'];

/**
 * Registry entries that carry an admin toggle but are never rendered in the
 * sidebar. NavigationAccessGate appends them to the sidebar's base list so a
 * switched-off toggle blocks the page even though no menu item exists.
 *
 * @param portalMode - which side of the portal the user is on
 * @returns the gate-only entries for that side
 */
export function buildGateOnlyNavigation(portalMode: PortalMode): NavItem[] {
  const order = portalMode === 'supplier' ? GATE_ONLY_SUPPLIER : GATE_ONLY_CUSTOMER;
  return order.map(key => ({ ...NAV_ITEMS[key] }));
}

/**
 * CR-7478: whether a nav item is hidden because the Dashboard Visibility tab
 * it depends on is switched off. A Compliance-mode tenant IS the Compliance
 * module, so its onboarding page never depends on the dashboard tab.
 */
export function isHiddenByDashboardTab(
  item: Pick<NavItem, 'requiresDashboardTab'>,
  isDashboardTabEnabled: (tabId: string) => boolean,
  isComplianceMode: boolean,
): boolean {
  if (!item.requiresDashboardTab || isComplianceMode) return false;
  return !isDashboardTabEnabled(item.requiresDashboardTab);
}

/** CR-7478: the same rule for the /onboarding route, so a direct visit matches the menu. */
export function isOnboardingPageHidden(
  isDashboardTabEnabled: (tabId: string) => boolean,
  isComplianceMode: boolean,
): boolean {
  return isHiddenByDashboardTab(NAV_ITEMS.onboarding, isDashboardTabEnabled, isComplianceMode);
}
